import {
  CallCreateProductWithInventoryRequest,
  CallCreateProductWithInventoryResponse,
  CallSetProductWeeklyPriceRequest,
  CallSetProductWeeklyPriceResponse,
  CallSyncFirestoreToBigQueryRequest,
  CallSyncFirestoreToBigQueryResponse,
  CallSyncProductPriceTrendRequest,
  CallSyncProductPriceTrendResponse,
  CallMaintainStoreRequest,
  CallMaintainStoreResponse,
  ChangeVolumeTemplate,
  InventoryIssueTransactionRequest,
  InventoryIssueTransactionResponse,
  InvoiceTemplate,
  ProductSoldOutTemplate,
  SupplierInboundTransactionBatchRequest,
  SupplierInboundTransactionBatchResponse,
} from '@gooduncles/gu-app-schema';
import {
  CallAdminChangeUserPasswordRequest,
  CallAdminChangeUserPasswordResponse,
  CallAdminLinkUserPhoneRequest,
  CallAdminLinkUserPhoneResponse,
  CallRevokeBiometricLinksRequest,
  CallRevokeBiometricLinksResponse,
  CallKakaoBrandMessageRequest,
  CallKakaoBrandMessageResponse,
  CallKakaoAlimtalkRequest,
  CallKakaoAlimtalkResponse,
  CallKakaoFriendtalkRequest,
  CallKakaoFriendtalkResponse,
  CallSendDailyPricePushRequest,
  CallSendDailyPricePushResponse,
  CallSendInvoicePushRequest,
  CallSendInvoicePushResponse,
  CallSendMobileMessageRequest,
  CallSendMobileMessageResponse,
  CallSetUserStatusRequest,
  CallSetUserStatusResponse,
  CallSyncKakaoBrandMessageRequest,
  CallSyncKakaoBrandMessageResponse,
  CallSyncKakaoTalkResultRequest,
  CallSyncKakaoTalkResultResponse,
  CallSyncMobileMessageResultRequest,
  CallSyncMobileMessageResultResponse,
} from '@gooduncles/gu-app-schema';
import { CallGetSeoulStoresRequest, CallGetSeoulStoresResponse } from 'src/schema/schema-seoul-store';

import { FirebaseManager } from 'src/utils/firebase/firebase-manager';

const firebaseManager = FirebaseManager.getInstance();

/**
 * 카카오 친구톡 발송
 */
export const callKakaoFriendtalk = firebaseManager.getCallable<CallKakaoFriendtalkRequest, CallKakaoFriendtalkResponse>(
  'callKakaoFriendtalk',
);

/**
 * 카카오 알림톡 발송 (제네릭)
 */
export const callSendKakaoAlimtalk = firebaseManager.getCallable<
  CallKakaoAlimtalkRequest<Record<string, string>>,
  CallKakaoAlimtalkResponse
>('callKakaoAlimtalk');

/**
 * 수량 변경 알림톡
 */
export const callSendKakaoAlimTalkForChangeVolume = firebaseManager.getCallable<
  CallKakaoAlimtalkRequest<ChangeVolumeTemplate>,
  CallKakaoAlimtalkResponse
>('callKakaoAlimtalk');

/**
 * 품절 알림톡
 */
export const callSendKakaoAlimTalkForProductSoldout = firebaseManager.getCallable<
  CallKakaoAlimtalkRequest<ProductSoldOutTemplate>,
  CallKakaoAlimtalkResponse
>('callKakaoAlimtalk');

/**
 * 청구안내 발송 알림톡
 */
export const callSendKakaoAlimTalkForInvoice = firebaseManager.getCallable<
  CallKakaoAlimtalkRequest<InvoiceTemplate>,
  CallKakaoAlimtalkResponse
>('callKakaoAlimtalk');

/**
 * 청구서 v2 푸시 발송 (serviceVersion === 2 매장 대상)
 * console이 명시적으로 고른 대상에 대해 ChannelMessage(store_{storeId})를 생성해 FCM을 트리거한다.
 * 딥링크는 평문 invoiceDetail._id 기반 `/invoices/{invoiceDetailId}` (토큰 불필요).
 */
export const callSendInvoicePush = firebaseManager.getCallable<CallSendInvoicePushRequest, CallSendInvoicePushResponse>(
  'callSendInvoicePush',
);

/**
 * 카카오 알림톡의 개별 결과 동기화
 */
export const callSyncKakaoTalkResult = firebaseManager.getCallable<
  CallSyncKakaoTalkResultRequest,
  CallSyncKakaoTalkResultResponse
>('callSyncKakaoTalkResult');

/**
 * SMS/LMS(한글 45자 이상의 장문은 LMS로 보낸다.) 발송
 */
export const callSendMobileMessage = firebaseManager.getCallable<
  CallSendMobileMessageRequest,
  CallSendMobileMessageResponse
>('callSendMobileMessage');

/**
 * MobileMessage의 개별 결과 동기화
 */
export const callSyncMobileMessageResult = firebaseManager.getCallable<
  CallSyncMobileMessageResultRequest,
  CallSyncMobileMessageResultResponse
>('callSyncMobileMessageResult');

/**
 * 서울시 일반음식점 인허가 정보 조회
 */
export const callGetSeoulStores = firebaseManager.getCallable<CallGetSeoulStoresRequest, CallGetSeoulStoresResponse>(
  'callGetSeoulStores',
);

/**
 * 고객 상태 변경
 */
export const callSetUserStatus = firebaseManager.getCallable<CallSetUserStatusRequest, CallSetUserStatusResponse>(
  'callSetUserStatus',
);

/**
 * 현재까지 생성된 가락시장 가격 동향과 자사의 매입가 동향을 최근 30일간의 데이터로 업데이트합니다.
 */
export const callSyncProductPriceTrend = firebaseManager.getCallable<
  CallSyncProductPriceTrendRequest,
  CallSyncProductPriceTrendResponse
>('callSyncProductPriceTrend');

/**
 * 상품의 주간 가격 데이터를 생성합니다.
 */
export const callSetProductWeeklyPrice = firebaseManager.getCallable<
  CallSetProductWeeklyPriceRequest,
  CallSetProductWeeklyPriceResponse
>('callSetProductWeeklyPrice');

/**
 * BigQuery의 테이블 초기화 및 데이터 추가 작업을 수행합니다.
 */
export const callSyncFirestoreToBigQuery = firebaseManager.getCallable<
  CallSyncFirestoreToBigQueryRequest,
  CallSyncFirestoreToBigQueryResponse
>('callSyncFirestoreToBigQuery');

/**
 * 거래처 입고 처리 요청
 */
export const callSupplierInboundTransactions = firebaseManager.getCallable<
  SupplierInboundTransactionBatchRequest,
  SupplierInboundTransactionBatchResponse
>('callSupplierInboundTransactions');

/**
 * 재고 이슈 처리 요청
 * body를 null로 주는경우 preflight로 처리된다.
 */
export const callInventoryIssueTransaction = firebaseManager.getCallable<
  InventoryIssueTransactionRequest,
  InventoryIssueTransactionResponse
>('callInventoryIssueTransaction');

/**
 * 매장 유지보수 작업 (메뉴 동기화, 검색 인덱스, 장소 정보 등)
 */
export const callMaintainStore = firebaseManager.getCallable<CallMaintainStoreRequest, CallMaintainStoreResponse>(
  'callMaintainStore',
);

/**
 * 상품 생성 요청
 */
export const callCreateProductWithInventory = firebaseManager.getCallable<
  CallCreateProductWithInventoryRequest,
  CallCreateProductWithInventoryResponse
>('callCreateProductWithInventory');

/**
 * 일일 가격 알림 발송 (v2 푸시 전용, 타임아웃 15분 - 서버 timeoutSeconds:900에 정렬)
 * serviceVersion=2 && subscribeDailyPrice=true 사용자에게 ChannelMessage(FCM 푸시)를 발송한다.
 * 기존 callSendDailyPriceListV2(v1 카카오 브랜드 메시지 + v2 푸시 통합)에서 v1 발송을 제거한 버전.
 */
export const callSendDailyPricePush = firebaseManager.getCallable<
  CallSendDailyPricePushRequest,
  CallSendDailyPricePushResponse
>('callSendDailyPricePush', { timeout: 900000 });

/**
 * 카카오 브랜드 메시지 발송
 */
export const callKakaoBrandMessage = firebaseManager.getCallable<
  CallKakaoBrandMessageRequest,
  CallKakaoBrandMessageResponse
>('callKakaoBrandMessage');

/**
 * 카카오 브랜드 메시지 결과 동기화
 */
export const callSyncKakaoBrandMessage = firebaseManager.getCallable<
  CallSyncKakaoBrandMessageRequest,
  CallSyncKakaoBrandMessageResponse
>('callSyncKakaoBrandMessage');

/**
 * 관리자가 SMS 인증 없이 고객 계정에 전화번호를 직접 연동.
 * 다른 계정이 이미 소유한 번호인 경우 1차 호출에서 PHONE_ALREADY_LINKED + existingLink를 반환하며,
 * 운영자 확인 후 transferFromUid를 명시해 재호출하면 강제 이전된다.
 */
export const callAdminLinkUserPhone = firebaseManager.getCallable<
  CallAdminLinkUserPhoneRequest,
  CallAdminLinkUserPhoneResponse
>('callAdminLinkUserPhone');

/**
 * 관리자가 SMS·전화번호 재연동으로도 복구 불가능한 사용자에 한해 Firebase Auth 비밀번호를 직접 변경.
 * v2 안정화 이후 제거 예정.
 */
export const callAdminChangeUserPassword = firebaseManager.getCallable<
  CallAdminChangeUserPasswordRequest,
  CallAdminChangeUserPasswordResponse
>('callAdminChangeUserPassword');

/**
 * 생체인증 연동(biometricLinks) 회수 — 운영자(admin/manager) 전용 kill switch.
 * 탈취된 deviceSecret을 무력화하는 유일한 실질적 수단. mode='byUid'로 한 사용자의 모든 매핑을 삭제한다.
 */
export const callRevokeBiometricLinks = firebaseManager.getCallable<
  CallRevokeBiometricLinksRequest,
  CallRevokeBiometricLinksResponse
>('callRevokeBiometricLinks');

/**
 * 매장 Algolia 검색 인덱스 동기화.
 * 인덱스에는 관리자(user)의 email·userTel이 denormalize되어 실리는데, user 문서에는 트리거가 없다.
 * 콘솔에서 관리자 연락처를 수정한 뒤 명시적으로 호출해야 인덱스가 최신 상태가 된다.
 * 예외를 throw하지 않고 result 필드로 성패를 반환한다.
 */
export const callSyncStoreAlgoliaIndex = firebaseManager.getCallable<
  { storeId: string },
  { result: 'success' | 'error'; reason?: string }
>('callSyncStoreAlgoliaIndex');
